import React from 'react';
import { Card } from 'primereact/card';
import { Divider } from 'primereact/divider';

import usePageTitle from '../../hooks/UsePageTitle';

const OperationalDueDiligence = () => {
  usePageTitle('Operational Due Diligence');

  return (
    <div className="page-container">
      <div className="grid">
        <div className="col-12">
          <h1 className="text-center">Operational Due Diligence</h1>
          <Divider />
        </div>

        {/* Introduction Section */}
        <div className="col-12 md:col-6">
          <img 
            src='/images/Operational Due Diligence.jpg' 
            alt="Operational Due Diligence" 
            className="w-full border-round shadow-2 mb-4" 
            style={{ maxHeight: '400px', objectFit: 'cover' }}
          />
        </div>
        <div className="col-12 md:col-6">
          <Card className="h-full">
            <p className="text-lg line-height-3">
              While financial statements and legal contracts are important, the real engine of any business 
              lies in its operations — the day-to-day activities that drive revenue, control costs, and 
              deliver value.
            </p>
            <p className="text-lg line-height-3 mt-3">
              At SnP Finvisors, our Operational Due Diligence (ODD) services are designed to uncover how 
              well a business really runs, whether it can scale sustainably, and how efficiently its people, 
              systems, and processes work together.
            </p>
            <p className="text-lg line-height-3 mt-3 font-semibold">
              Unlike financial audits that focus on "what happened," ODD focuses on how things happen — 
              identifying weaknesses, inefficiencies, risks, and opportunities embedded deep within business operations.
            </p>
          </Card>
        </div>

        {/* Why ODD is Vital Section */}
        <div className="col-12 mt-5">
          <h2>Why Operational Due Diligence is Vital</h2>
          <Divider />
          <div className="flex flex-column md:flex-row gap-5">
            <div className="flex-1">
              <p className="text-xl line-height-3">
                Imagine buying a well-branded school chain, only to find later that attendance tracking is manual, 
                vendor payments are delayed, teacher attrition is high, and inventory losses go unnoticed. These are 
                not accounting issues — they are operational risks that can destroy value post-transaction.
              </p>
              <p className="text-xl line-height-3 mt-3">
                ODD protects you from such surprises by giving you insight into:
              </p>
              <ul className="text-xl line-height-3 ml-4">
                <li className="mb-2">The strength of business processes and controls</li>
                <li className="mb-2">The effectiveness of internal teams and leadership</li>
                <li className="mb-2">Technology infrastructure, automation gaps, and system integration</li>
                <li className="mb-2">Vendor dependencies and operational bottlenecks</li>
                <li>Scalability, replicability, and business continuity</li>
              </ul>
            </div>
            <img 
              src='/images/Tax Due Diligence.jpg' 
              alt="Operational Analysis" 
              className="w-full md:w-6 border-round shadow-2" 
              style={{ maxHeight: '400px', objectFit: 'cover' }}
            />
          </div>
        </div>

        {/* Framework Section */}
        <div className="col-12 mt-5">
          <h2>Our Operational Due Diligence Framework</h2>
          <p className="text-xl mb-4">
            Our ODD approach is both strategic and hands-on. We don't just review SOPs on paper — we observe, 
            interview, test, and assess actual workflows and operational discipline across functions.
          </p>
          <Divider />
          
          <div className="grid">
            {/* Framework Item 1 */}
            <div className="col-12 md:col-6 lg:col-4">
              <Card title="1. Process & Workflow Review" className="h-full">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Mapping of end-to-end business processes: procurement, production, delivery, sales, service, and finance.</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Identification of manual vs. automated processes.</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Detection of process duplications, delays, leakages, or over-dependencies on individuals.</span>
                  </li>
                </ul>
              </Card>
            </div>

            {/* Framework Item 2 */}
            <div className="col-12 md:col-6 lg:col-4">
              <Card title="2. Human Resource & Organizational Analysis" className="h-full">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Review of organizational structure, team bandwidth, and role clarity.</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>HR policies, hiring patterns, attrition rates, and training mechanisms.</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Identification of skill gaps, succession issues, and management dependencies.</span>
                  </li>
                </ul>
              </Card>
            </div>

            {/* Framework Item 3 */}
            <div className="col-12 md:col-6 lg:col-4">
              <Card title="3. Technology & ERP Assessment" className="h-full">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Evaluation of current technology landscape — ERP, CRM, POS, accounting software, etc.</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Review of system integration across departments and locations.</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Cybersecurity readiness, data backup protocols, and system reliability.</span>
                  </li>
                </ul>
              </Card>
            </div>

            {/* Framework Item 4 */}
            <div className="col-12 md:col-6 lg:col-4">
              <Card title="4. Vendor, Customer & Supply Chain Evaluation" className="h-full">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Analysis of vendor concentration, contract terms, pricing fluctuations, and lead times.</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Customer dependency risks, payment behavior, and satisfaction mechanisms.</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Inventory cycles, stock-out frequency, warehousing practices, and logistics efficiency.</span>
                  </li>
                </ul>
              </Card>
            </div>

            {/* Framework Item 5 */}
            <div className="col-12 md:col-6 lg:col-4">
              <Card title="5. Internal Control & SOP Review" className="h-full">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Verification of internal Standard Operating Procedures (SOPs) and their on-ground adherence.</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Testing of control checkpoints in procurement, sales approvals, expenses, and payroll.</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Identification of weak controls leading to fraud, wastage, or audit challenges.</span>
                  </li>
                </ul>
              </Card>
            </div>

            {/* Framework Item 6 */}
            <div className="col-12 md:col-6 lg:col-4">
              <Card title="6. Operational Metrics & KPIs" className="h-full">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Analysis of key operational indicators (e.g., turnaround time, cost per unit, downtime, throughput).</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Comparison of actual KPIs against industry benchmarks.</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Recommendations for monitoring, reporting, and improvement.</span>
                  </li>
                </ul>
              </Card>
            </div>

            {/* Framework Item 7 */}
            <div className="col-12 md:col-6 lg:col-4">
              <Card title="7. Customer Experience & Service Delivery" className="h-full">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Assessment of service quality, delivery timeliness, and customer grievance redressal.</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Mystery audits or stakeholder interviews (where permitted) to evaluate perception gaps.</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Review of customer retention, engagement, and referral models.</span>
                  </li>
                </ul>
              </Card>
            </div>

            {/* Framework Item 8 */}
            <div className="col-12 md:col-6 lg:col-4">
              <Card title="8. Scalability & Business Continuity" className="h-full">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Evaluation of whether existing infrastructure and processes can handle projected growth.</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Identification of geographical or operational constraints.</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Review of disaster recovery, crisis management, and business continuity planning.</span>
                  </li>
                </ul>
              </Card>
            </div>

            {/* Framework Item 9 */}
            <div className="col-12 md:col-6 lg:col-4">
              <Card title="9. Red Flag Reporting & Actionable Insights" className="h-full">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Summary of operational risks that could affect valuation, integration, or ongoing performance.</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Quick wins for improvement and long-term operational transformation roadmaps.</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Support for post-transaction integration or process harmonization.</span>
                  </li>
                </ul>
              </Card>
            </div>
          </div>
        </div>

        {/* Closing Section */}
        <div className="col-12 mt-5">
          <div className="flex flex-column md:flex-row gap-5">
            <div className="flex-1">
              <h2>Operational Excellence for Sustainable Value</h2>
              <Divider />
              <p className="text-xl line-height-3">
                At SnP Finvisors, we go beyond the numbers to reveal the true operational health of a business. 
                Our Operational Due Diligence provides the critical insights you need to understand how a company 
                really works, identify hidden risks and opportunities, and make investment decisions with confidence.
              </p>
              <p className="text-xl line-height-3 mt-3 font-semibold">
                Because sustainable value isn't just about what a business earns — it's about how it operates.
              </p>
            </div>
            <img 
              src='/images/EES.jpg'
              alt="Operational Excellence" 
              className="w-full md:w-6 border-round shadow-2" 
              style={{ maxHeight: '350px', objectFit: 'cover' }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default OperationalDueDiligence;